import { template as _$template } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<canvas class=flight-loading__canvas>`);
import { createEffect, createResource, onCleanup, onMount } from "solid-js";
import { paintOps } from "../shell/render/canvas-painter.ts";
import { createShellSurface } from "../shell/render/shell-surface.ts";
import { loadShellFonts, paletteOnlyAssets } from "../shell/render/shell-assets.ts";
import { loadPsPalette } from "../shell/render/ps-palette.ts";
import { bootBarFraction, bootPhaseCaption } from "./boot-progress.ts";
import { barWidth, loadingScreenOps } from "./loading-screen.ts";

/**
 * How often the bar is recomputed. The easing inside a phase
 * (`bootBarFraction`) is continuous, but the bar is 212 logical pixels wide over
 * a boot of roughly 1.7 s, so it advances about one pixel per 8 ms — a repaint
 * every 80 ms is already finer than the pixel grid at the fastest the bar ever
 * moves, and `requestAnimationFrame` would be sixteen times the work on a thread
 * the boot itself is trying to use.
 */
const TICK_MS = 80;

/**
 * `/fly`'s loading screen: the shell's own 320×200 frame with a bar driven by
 * `scene-boot.ts`'s phase reports (`loading-screen.ts` draws it,
 * `boot-progress.ts` decides where it stands).
 *
 * The component owns three things and decides nothing: the canvas, the asset
 * loads, and the repaint. That is the same division `map/MapOverlay.tsx` uses,
 * and for the same reason — everything about what the screen looks like is pure
 * and testable without a browser.
 *
 * **It never waits for its own artwork.** A loading screen that made the player
 * wait before admitting it was loading would be worse than none, so it comes up
 * in three stages and each is drawable on its own: black on mount, with nothing
 * fetched; the plate and the bar as soon as `PS.PAL` lands (three kilobytes);
 * the two lines when the font atlases do (the bake manifest, which is 57 KB on
 * the wire in production but 1.1 MB from the dev server). Coming in from a
 * briefing screen the fonts are already in hand — that load is memoised — and
 * all three stages happen on the first paint.
 */
export function FlightLoadingScreen(props) {
  const surface = createShellSurface();
  // Two stages, cheapest first: the ramp alone is enough to draw the plate and
  // the bar, and the atlases behind it add the words (`loadingScreenOps`).
  const [palette] = createResource(loadPsPalette);
  const [assets] = createResource(loadShellFonts);
  let canvasRef;
  /** When the phase now being reported began — the clock the easing runs on. */
  let phaseStartedMs = performance.now();
  /** What is on the canvas, so a tick that changes nothing costs nothing. */
  let painted = "";
  function repaint() {
    if (!canvasRef) return;
    const loaded = assets();
    const ramp = palette();
    // The fonts carry their own palette, so they are preferred whole; the ramp
    // on its own is the earlier, wordless stage.
    const base = loaded ?? (ramp ? paletteOnlyAssets(ramp) : undefined);
    if (!base) return;
    const title = base.fonts.get("FONT1");
    const phaseFont = base.fonts.get("FONT3");
    const fonts = title && phaseFont ? {
      title,
      phase: phaseFont
    } : undefined;
    const progress = props.progress;
    const elapsed = performance.now() - phaseStartedMs;
    // The caption, not the bare label: past its budget a phase says how long it
    // has been running, which is the only thing on this screen still moving once
    // the easing has run out of segment (`bootPhaseCaption`).
    const label = progress ? bootPhaseCaption(progress.phase, elapsed) : "";
    const fraction = progress ? bootBarFraction(progress.phase, elapsed, progress.bytes) : 0;
    // Keyed on the *drawn* width rather than the fraction: between two ticks the
    // fraction always differs and the bar usually does not. The caption is in the
    // key as well, so the ticking second repaints even when the bar cannot move.
    const key = `${fonts ? label : ""}:${barWidth(fraction)}`;
    if (key === painted) return;
    painted = key;
    paintOps(surface.logical, base, loadingScreenOps(label, fraction, fonts, progress?.note));
    surface.blit();
  }
  onMount(() => {
    if (!canvasRef) return;
    surface.attach(canvasRef);
    // Black immediately, before anything is loaded — the frame the original's
    // own screen changes hold, and the thing this screen must never fail to do.
    surface.clear();
    const observer = new ResizeObserver(() => {
      painted = "";
      repaint();
      surface.blit();
    });
    observer.observe(canvasRef);
    const timer = setInterval(repaint, TICK_MS);
    onCleanup(() => {
      observer.disconnect();
      clearInterval(timer);
    });
  });

  // A new phase restarts the easing clock, so each segment eases across its own
  // share from its own start rather than from the boot's. Keyed on the phase
  // itself rather than on the object: a report carrying the same phase would
  // otherwise rewind the clock and the bar would stutter backwards.
  createEffect(previous => {
    const phase = props.progress?.phase;
    if (phase !== previous) {
      phaseStartedMs = performance.now();
      repaint();
    }
    return phase;
  });

  // Either load arriving is the other thing that can turn a black frame into a
  // screen: the ramp brings the plate and the bar, the atlases bring the words.
  createEffect(() => {
    if (palette() || assets()) repaint();
  });
  return (() => {
    var _el$ = _tmpl$();
    _$use(element => canvasRef = element, _el$);
    return _el$;
  })();
}